/**
 * Escapes a value for use in CSV files
 * Wraps values containing commas, quotes, or newlines in quotes and escapes internal quotes
 */
export const escapeCsvValue = (value: string | number | null | undefined): string => {
	if (value === null || value === undefined) return "";
	const stringValue = String(value);
	if (/[",\n]/.test(stringValue)) {
		return `"${stringValue.replace(/"/g, '""')}"`;
	}
	return stringValue;
};

/**
 * Safely parses a value to a number
 * Returns undefined if the value cannot be parsed to a finite number
 */
export const parseNumber = (value?: string | number): number | undefined => {
	if (value === undefined || value === null) {
		return undefined;
	}
	const parsed = typeof value === "number" ? value : Number(value);
	return Number.isFinite(parsed) ? parsed : undefined;
};

/**
 * Exports data to a CSV file and triggers a download
 * @param headers - Array of column headers
 * @param rows - Array of data rows (each row is an array of values)
 * @param filename - Name of the downloaded file (without .csv extension)
 */
export const exportToCsv = (
	headers: string[],
	rows: Array<Array<string | number | null | undefined>>,
	filename: string
): void => {
	const csvContent = [headers, ...rows].map((row) => row.join(",")).join("\n");
	const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
	const url = URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.download = `${filename}.csv`;
	link.click();
	URL.revokeObjectURL(url);
};

/**
 * Triggers a file download from a Blob
 * @param blob - The blob to download
 * @param filename - Full filename including extension
 */
export const downloadBlob = (blob: Blob, filename: string): void => {
	const url = URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.download = filename;
	link.click();
	URL.revokeObjectURL(url);
};

/**
 * Creates a CSV export handler that filters data by selected IDs
 * @param allData - Complete dataset
 * @param selectedIds - IDs of items to export
 * @param mapRowToCsv - Function to map a data item to CSV row values
 * @param headers - CSV column headers
 * @param filename - Name of the downloaded file (without .csv extension)
 */
export const exportSelectedToCsv = <T extends { id: string | number }>(
	allData: T[],
	selectedIds: Array<string | number>,
	mapRowToCsv: (item: T) => Array<string | number | null | undefined>,
	headers: string[],
	filename: string
): void => {
	if (!selectedIds.length) return;

	const selectedIdsSet = new Set(selectedIds.map((id) => String(id)));
	const selectedData = allData.filter((item) => selectedIdsSet.has(String(item.id)));

	if (!selectedData.length) return;

	const rows = selectedData.map(mapRowToCsv);
	exportToCsv(headers, rows, filename);
};
